export type SurveyStatus = 'draft' | 'in_progress' | 'completed';

export type SurveyView = 'board' | 'editor' | 'results';

export interface SurveyQuestion {
  id: string;
  survey_id: string;
  question_text: string;
  position: number;
  created_at: string;
}

export interface SurveyRecipient {
  id: string;
  survey_id: string;
  email: string;
  name: string | null;
  status: 'pending' | 'completed';
  source: 'invite' | 'link';
  completed_at: string | null;
  created_at: string;
}

export interface SurveySummary {
  id: string;
  title: string | null;
  status: SurveyStatus;
  deadline: string | null;
  created_by: string;
  creator_name: string | null;
  recipient_count: number;
  response_count: number;
  question_count: number;
  created_at: string;
  updated_at: string;
}

export interface SurveyDetail {
  id: string;
  title: string | null;
  status: SurveyStatus;
  deadline: string | null;
  created_by: string;
  creator_name: string | null;
  org_id: string;
  tenant_id: string;
  ai_summary: string | null;
  ai_summary_generated_at: string | null;
  public_link_enabled: boolean;
  public_link_token: string | null;
  questions: SurveyQuestion[];
  recipients: SurveyRecipient[];
  created_at: string;
  updated_at: string;
}

export interface SurveyAnswerResponse {
  id: string;
  recipient_id: string;
  question_id: string;
  answer_text: string;
  question_text: string | null;
  created_at: string;
}

export interface RecipientResult {
  recipient_id: string;
  email: string;
  name: string | null;
  status: string;
  source: 'invite' | 'link';
  completed_at: string | null;
  answers: SurveyAnswerResponse[];
}

export interface SurveyResults {
  survey_id: string;
  title: string | null;
  recipients: RecipientResult[];
}

export interface SurveyRespondContext {
  survey_id: string;
  title: string | null;
  questions: SurveyQuestion[];
  already_completed: boolean;
  is_public_link: boolean;
}

export interface ExtractedEmails {
  emails: string[];
  source_filename: string;
}

export type Multilingual<T> = T | { en: T; es: T; pt: T };

export interface AiQuestionSummary {
  question: string;
  summary: Multilingual<string>;
  key_themes: Multilingual<string[]>;
  sentiment: 'positive' | 'negative' | 'mixed' | 'neutral';
  notable_quotes: string[];
}

export interface AiSurveySummary {
  overview: Multilingual<string>;
  total_responses: number;
  question_summaries: AiQuestionSummary[];
  takeaways: Multilingual<string[]>;
}

export function resolveML<T>(value: Multilingual<T>, lang: string): T {
  if (value && typeof value === 'object' && 'en' in value) {
    return (value as Record<string, T>)[lang] ?? (value as Record<string, T>).en;
  }
  return value as T;
}

export interface MappedAnswer {
  question_id: string;
  question_text: string;
  answer_text: string;
}

export interface AiMapAnswersResponse {
  confidence: number;
  mapped_answers: MappedAnswer[];
  message: string | null;
}
